import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Boxes, FileText, Truck, AlertTriangle, Scissors, PlusCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NegativeSoles } from "@/components/NegativeSoles";
import { UpcomingDeliveries } from "@/components/UpcomingDeliveries";
import { BackupReminder } from "@/components/BackupReminder";

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="relative overflow-hidden rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5 group hover:border-[#D97706]/60 transition-colors"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[11px] font-mono uppercase tracking-widest text-[#6B5B4A]">{label}</p>
        <p className="font-display text-4xl font-extrabold mt-2 tabular-nums">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
    <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-gradient-to-br from-[#D97706]/10 to-transparent" />
  </div>
);

export default function Dashboard() {
  const { productById } = useData();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Üretim Panosu</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight">
            Kontrol Paneli
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Kesimden sevkiyata — atölyenin anlık durumu</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="quick-actions">
        <Link to="/kesim">
          <Button data-testid="quick-cut-btn" className="w-full h-14 bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide text-base justify-start px-5">
            <Scissors className="w-5 h-5 mr-3" /> Kesim Girişi
            <ArrowRight className="w-4 h-4 ml-auto" />
          </Button>
        </Link>
        <Link to="/siparis">
          <Button data-testid="quick-order-btn" variant="outline" className="w-full h-14 border-[#DCCFB8] bg-[#FFFBF4] hover:bg-[#EFE6D6] text-[#2B2118] hover:text-[#B45309] font-display uppercase tracking-wide text-base justify-start px-5">
            <PlusCircle className="w-5 h-5 mr-3 text-[#B45309]" /> Yeni Sipariş
            <ArrowRight className="w-4 h-4 ml-auto" />
          </Button>
        </Link>
        <Link to="/sevkiyat">
          <Button data-testid="quick-ship-btn" variant="outline" className="w-full h-14 border-[#DCCFB8] bg-[#FFFBF4] hover:bg-[#EFE6D6] text-[#2B2118] hover:text-[#B45309] font-display uppercase tracking-wide text-base justify-start px-5">
            <Truck className="w-5 h-5 mr-3 text-[#B45309]" /> Sevkiyat Çıkışı
            <ArrowRight className="w-4 h-4 ml-auto" />
          </Button>
        </Link>
      </div>

      <BackupReminder last={data?.last_backup} loading={loading || !data} />
      <UpcomingDeliveries items={data?.upcoming_deliveries} />
      <NegativeSoles items={data?.negative_soles} />

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testId="stat-total-stock" icon={Boxes} label="Toplam Stok (Çift)" value={loading ? "—" : (data?.total_stock ?? 0)} accent="bg-[#D97706]" />
        <StatCard testId="stat-pending-orders" icon={FileText} label="Bekleyen Sipariş" value={loading ? "—" : `${data?.pending_orders ?? 0}`} accent="bg-[#3B82F6]" />
        <StatCard testId="stat-week-shipped" icon={Truck} label="Bu Hafta Sevk" value={loading ? "—" : (data?.week_shipped ?? 0)} accent="bg-[#047857]" />
        <StatCard testId="stat-low-stock" icon={AlertTriangle} label="Kritik Stok" value={loading ? "—" : (data?.low_stock_count ?? 0)} accent="bg-[#EF4444]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent cuttings */}
        <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5" data-testid="recent-cuttings-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold uppercase tracking-tight">Son Kesimler</h2>
            <Scissors className="w-4 h-4 text-[#B45309]" />
          </div>
          <div className="space-y-2">
            {(data?.recent_cuttings ?? []).length === 0 && (
              <p className="text-sm text-[#8C7B68]">Henüz kesim girişi yok</p>
            )}
            {(data?.recent_cuttings ?? []).map((c) => {
              const total = c.items.reduce((a, b) => a + b.qty, 0);
              return (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#E8DDCA] last:border-0">
                  <div>
                    <p className="text-sm font-medium">{c.cutter_name || "Kesici"}</p>
                    <p className="text-[11px] font-mono text-[#8C7B68] uppercase">{new Date(c.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                  <span className="font-mono text-lg font-semibold text-[#B45309]">+{total}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Recent shipments */}
        <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5" data-testid="recent-shipments-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold uppercase tracking-tight">Son Sevkiyatlar</h2>
            <Truck className="w-4 h-4 text-[#047857]" />
          </div>
          <div className="space-y-2">
            {(data?.recent_shipments ?? []).length === 0 && (
              <p className="text-sm text-[#8C7B68]">Henüz sevkiyat yok</p>
            )}
            {(data?.recent_shipments ?? []).map((s) => {
              const total = s.items.reduce((a, b) => a + b.qty, 0);
              return (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-[#E8DDCA] last:border-0">
                  <div>
                    <p className="text-sm font-medium">{s.customer_name || "Müşteri"}</p>
                    <p className="text-[11px] font-mono text-[#8C7B68] uppercase">{new Date(s.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                  <span className="font-mono text-lg font-semibold text-[#047857]">−{total}</span>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Low stock */}
      {data?.low_stock?.length > 0 && (
        <section className="mt-5 rounded-lg border border-[#EF4444]/40 bg-[#EFE6D6] p-5" data-testid="low-stock-card">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            <h2 className="font-display text-xl font-bold uppercase tracking-tight">Kritik Stok Uyarıları</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {data.low_stock.map((s) => (
              <div key={s.key} className="rounded-md border border-[#DCCFB8] bg-[#FFFBF4] px-3 py-2">
                <p className="text-xs font-medium truncate">{productById(s.product_id)?.model_code || "—"}</p>
                <p className="text-[10px] font-mono uppercase text-[#6B5B4A]">{productById(s.product_id)?.model_color} · N°{s.size}</p>
                <p className="font-mono text-lg font-bold text-[#EF4444]">{s.qty}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
